import React from 'react'
import Link from 'next/link'
import styled from '@emotion/styled'

import { COLORS, PAGE_BREAKPOINTS, ROUTES } from 'parameters/index'
import {
	CreditCardsIcon,
	LifeInsuranceIcon,
	MortgageIcon,
	PersonalLoansIcon,
	SavingsIcon,
	StudentLoanRefinanceIcon
} from 'components/layout/icons'

interface Props {
	title?: string
	subTitle?: string
	hideMortgage?: boolean
}

const EmptyState = ({ title, subTitle, hideMortgage }: Props): JSX.Element => {
	return (
		<EmptyStateWrapper className="emptyWrapper">
			{title ? (
				<Title className="emptyTitle">{title}</Title>
			) : (
				<Title className="emptyTitle">Let’s match you with the financial product that works best for your needs!</Title>
			)}
			{subTitle ? <SubTitle>{subTitle}</SubTitle> : <SubTitle>Choose a product below</SubTitle>}
			<Images className="emptyImages">
				<Link
					href={{
						pathname: `${ROUTES.NATIVE_LOANS}`,
						query: { productType: 'loan' }
					}}
					legacyBehavior
				>
					<Item className="emptyItem">
						<ImageContainer className="productImage">
							<PersonalLoansIcon />
						</ImageContainer>
						<span>Personal Loans</span>
					</Item>
				</Link>
				<Link href={ROUTES.LIFE_INSURANCE} legacyBehavior>
					<Item className="emptyItem">
						<ImageContainer className="productImage">
							<LifeInsuranceIcon />
						</ImageContainer>
						<span>Life Insurance</span>
					</Item>
				</Link>
				<Link href={ROUTES.CREDIT_CARDS} legacyBehavior>
					<Item className="emptyItem">
						<ImageContainer className="productImage">
							<CreditCardsIcon />
						</ImageContainer>
						<span>Credit Cards</span>
					</Item>
				</Link>
				<Link href={ROUTES.STUDENT_LOAN_REFINANCE} legacyBehavior>
					<Item className="emptyItem">
						<ImageContainer className="productImage">
							<StudentLoanRefinanceIcon />
						</ImageContainer>
						<span>
							Student Loan
							<br />
							Refinancing
						</span>
					</Item>
				</Link>
				<Link href={ROUTES.SAVINGS} legacyBehavior>
					<Item className="emptyItem">
						<ImageContainer className="productImage">
							<SavingsIcon />
						</ImageContainer>
						<span>Savings</span>
					</Item>
				</Link>
				{!hideMortgage && (
					<Link href={ROUTES.MORTGAGE} legacyBehavior>
						<Item className="emptyItem">
							<ImageContainer className="productImage">
								<MortgageIcon />
							</ImageContainer>
							<span>Mortgages</span>
						</Item>
					</Link>
				)}
			</Images>
		</EmptyStateWrapper>
	)
}

export default EmptyState

const EmptyStateWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	border: solid 2px #e3e3e3;
	background-color: #f4f4f4;
	width: 100%;
	padding: 60px 24px;
	flex: 1;
	height: 100%;
	max-height: 413px;

	@media screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		padding: 24px;
		max-height: unset;
		height: fit-content;
	}
`

const Title = styled.span`
	font-size: 24px;
	font-weight: bold;
	margin-bottom: 20px;
	text-align: center;
	width: 75%;
	@media screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		width: 100%;
		font-size: 16px;
		margin-bottom: 20px;
	}
`

const SubTitle = styled.span`
	font-size: 16px;
	display: block;
	margin-bottom: 12px;
	text-align: center;
`

const Images = styled.div`
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	width: 100%;
	@media screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		width: 100%;
		flex-wrap: wrap;
		justify-content: center;
	}
`

const ImageContainer = styled.div`
	width: 72px;
	height: 72px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
	background-color: ${COLORS.WHITE};
	margin-bottom: 16px;
	transition: 0.3s;

	padding: 16px;
`

const Item = styled.a`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	padding: 10px 0;
	width: 100px;
	@media screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		width: 100px;
	}
	@media screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		width: 90px;
	}
	@media screen and (${PAGE_BREAKPOINTS.MOBILE_SMALL}) {
		width: 33%;
	}
	span {
		font-size: 12px;
		font-weight: bold;
		text-align: center;
		width: 95%;
		@media screen and (${PAGE_BREAKPOINTS.MOBILE}) {
			width: 90%;
		}
	}

	&:hover {
		.productImage {
			box-shadow: 0 1.5px 3px 0 rgba(0, 0, 0, 0.1);
			transform: translateY(-5px);
		}
	}
`
